import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import CurrencyIcon from '../../../../components/CurrencyIcon';
import { CryptodraftContext } from '../../../../contexts/card/cryptodraft/context';
import { currencies, exchange, walletTypes } from '../../../../utilities/constants';
import {
  formatDecimalNumber,
  formatNumber,
  formatNumberWithComma,
} from '../../../../utilities/formatHelpers';
import { validateNumbers } from '../../../../utilities/formValidation';
import { showAlert } from '../../../../utilities/notifications';
import { useRate } from '../../../../utilities/sharedHooks/rates';
import FocusableInputArea from '../../../Accounts/partials/FocusableInputArea';
import {
  CARD_CRYPTODRAFT_PAY_PARTIAL,
  CARD_CRYPTODRAFT_PAY_SETTLEMENT,
  CARD_CRYPTODRAFT_WITHDRAWABLE,
} from '../../mutations';
import { cryptodraftScreens, forceUpdateOptions } from '../constants';
import MainBodyLayout from '../partials/MainBodyLayout';

const SettleCryptodraftPayPartial = () => {
  const { t: translate } = useTranslation();

  const { state, openScreen, forceUpdateAction } = useContext(CryptodraftContext);

  const cardCryptodraftDetails = useMemo(
    () => state?.cryptodraftDetails ?? {},
    [state?.cryptodraftDetails]
  );
  const cryptodraftSettlementDetails = state?.cryptodraftSettlementDetails;
  const principalCurrency = state?.principalCurrency;
  const fiatCurrency = state?.fiatCurrency;
  const coin = state?.selectedSettlementWallet?.coin;
  const availableBalance = state?.selectedSettlementWallet?.available_balance;

  const manualInputSpan = useRef();

  const { mutate: getWithdrawable, data: withdrawableAmount } = useMutation(
    CARD_CRYPTODRAFT_WITHDRAWABLE,
    {
      onSettled: (data, error) => {
        if (error) {
          showAlert({ message: error.message, type: 'danger' });
        }
      },
    }
  );

  const { mutate: payPartial, isLoading } = useMutation(CARD_CRYPTODRAFT_PAY_PARTIAL, {
    onSettled: (data, error) => {
      if (error) {
        showAlert({ message: error.message, type: 'danger' });
      } else {
        forceUpdateAction(forceUpdateOptions.DETAILS, true);
        forceUpdateAction(forceUpdateOptions.SETTLEMENT_DETAILS, true);
        showAlert({
          message: translate('cryptodraft_partial_settlement_success_notification_text'),
          type: 'success',
        });
        openScreen(cryptodraftScreens.WITHDRAW);
      }
    },
  });

  const { mutate: payFullSettlement, isLoading: isLoadingPayFullSettlement } = useMutation(
    CARD_CRYPTODRAFT_PAY_SETTLEMENT,
    {
      onSettled: (data, error) => {
        if (error) {
          showAlert({ message: error.message, type: 'danger' });
        } else {
          forceUpdateAction(forceUpdateOptions.DETAILS, true);
          showAlert({
            message: translate('cryptodraft_full_settlement_success_notification_text'),
            type: 'success',
          });
          openScreen(cryptodraftScreens.WITHDRAW);
        }
      },
    }
  );

  const validBalance =
    formatNumber(availableBalance * currencies[coin?.toUpperCase()].conversion) > 0;
  const rate = useRate(coin, fiatCurrency);

  const rateCryptoToCollateralPrincipleCurrency = useRate(
    coin,
    cardCryptodraftDetails?.principal_currency
  );

  const cryptoRate = useRate(cardCryptodraftDetails?.principal_currency, coin);

  const collateralPrincipleCurrencyToDisplayCurrencyRate = useRate(
    cardCryptodraftDetails?.principal_currency,
    principalCurrency
  );

  const toCollateralCurrencyRate = useRate(coin, cardCryptodraftDetails?.collateral_currency);

  const minDeposit = 0;
  const [maxDeposit, setMaxDeposit] = useState(100);
  const [manualInputWidth, setManualInputWidth] = useState(0);

  const [cryptoAmount, setCryptoAmount] = useState(minDeposit);
  const [fiatAmount, setFiatAmount] = useState(0);

  const calculatePrincipleOwedAfterPay = (cryptoAmountValue) => {
    const amountInCollateralCurrency = cryptoAmountValue * toCollateralCurrencyRate;
    const fiatAmountValue = amountInCollateralCurrency * rateCryptoToCollateralPrincipleCurrency;
    const fiatAmountBalance = fiatAmountValue / currencies[fiatCurrency].conversion;
    const principalOwedBalance =
      cryptodraftSettlementDetails?.principal_owed / currencies[fiatCurrency].conversion;
    const balanceAfterPay = principalOwedBalance - fiatAmountBalance;
    const fiatAmountAfterPay = formatNumber(
      balanceAfterPay * currencies[fiatCurrency].conversion,
      exchange.FIAT_VALUE
    );
    return fiatAmountAfterPay;
  };

  useEffect(() => {
    forceUpdateAction(forceUpdateOptions.SETTLEMENT_DETAILS, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const charactersLength =
      manualInputSpan.current.value.length > 1 ? manualInputSpan.current.value.length : 2;
    setManualInputWidth(charactersLength);
  }, [cryptoAmount]);

  useEffect(() => {
    if (cardCryptodraftDetails && cardCryptodraftDetails?.principal_net_amount) {
      setMaxDeposit(
        formatNumber(
          cardCryptodraftDetails?.principal_net_amount *
            currencies[fiatCurrency].conversion *
            cryptoRate,
          exchange.CRYPTO_VALUE
        )
      );
    }
  }, [cardCryptodraftDetails, cryptoRate, fiatCurrency]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      const principalAfterPartialPay = calculatePrincipleOwedAfterPay(cryptoAmount);
      getWithdrawable(principalAfterPartialPay);
    }, 1000);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cryptoAmount]);

  const handleChangeAmount = (event) => {
    const { value: enteredAmount } = event.target;

    const { decimalAmount: enteredAmountDecimal, formattedAmount: enteredAmountFormatted } =
      formatDecimalNumber(enteredAmount, 0);

    if (
      !validateNumbers(
        formatNumber(enteredAmountFormatted, exchange.CRYPTO_VALUE),
        walletTypes.CRYPTO
      )
    ) {
      return;
    }

    if (!enteredAmountDecimal || !enteredAmountFormatted) {
      setCryptoAmount(0);
      setFiatAmount(0);
    }

    const calcFiat = formatNumber(
      enteredAmountDecimal * rateCryptoToCollateralPrincipleCurrency,
      exchange.FIAT_VALUE
    );

    const formattedFiatCalc = formatDecimalNumber(calcFiat);

    setFiatAmount(formattedFiatCalc.decimalAmount);
    setCryptoAmount(enteredAmountDecimal);
  };

  const handleFocus = (e) => {
    if (!e.target) return;
    e.target.select();
    if (e.target.closest('.amount-input')) {
      e.target.closest('.amount-input').classList.add('active');
    }
  };

  const handleBlurAmount = (e) => {
    if (!e.target) return;
    if (e.target.closest('.amount-input')) {
      e.target.closest('.amount-input').classList.remove('active');
    }
  };

  const handleSubmit = () => {
    const maxDepositValue = maxDeposit * currencies[coin?.toUpperCase()].conversion;
    const amountValue = cryptoAmount * currencies[coin?.toUpperCase()].conversion;

    if (maxDepositValue === amountValue) {
      payFullSettlement({ coin });
    } else {
      payPartial({ coin, amount: fiatAmount });
    }
  };

  return (
    <MainBodyLayout
      withBack
      withInfoIcon
      onPrevClick={() => openScreen(cryptodraftScreens.SETTLE)}
      title={translate('pay_partial_btn_title')}>
      <div className="mt-5">
        <p className="text-f6 text-shadow p-0">
          {translate('cryptodraft_calculator_choose_source')}
        </p>
      </div>
      <div className="select-group bg-lighter rounded-extra p-3 d-flex flex-row mt-2 mb-4 select-currency-container">
        <div
          role="button"
          tabIndex="0"
          className="d-flex flex-row align-items-center px-2"
          onClick={() =>
            openScreen(cryptodraftScreens.WALLETS, { prevScreen: cryptodraftScreens.PAY_PARTIAL })
          }
          onKeyDown={() =>
            openScreen(cryptodraftScreens.WALLETS, { prevScreen: cryptodraftScreens.PAY_PARTIAL })
          }>
          <CurrencyIcon currencyCode={coin} className=" me-2" />
          <p className="m-0 me-2 text-f5 upper-case">{coin}</p>
          <i className="icon icon-arrow-down-navigation" />
        </div>
        <span className="text-right flex-fill">
          <b className="text-f5 m-0">
            {currencies[coin?.toUpperCase()].decimal === exchange.FIAT_VALUE
              ? formatNumberWithComma(
                  formatNumber(
                    availableBalance * currencies[coin?.toUpperCase()].conversion,
                    currencies[coin?.toUpperCase()].decimal
                  )
                )
              : formatNumber(
                  availableBalance * currencies[coin?.toUpperCase()].conversion,
                  currencies[coin?.toUpperCase()].decimal
                )}{' '}
            {coin?.toUpperCase()}
          </b>
          <p className="text-shadow text-f6 m-0">
            {formatNumberWithComma(
              formatNumber(
                availableBalance *
                  rate *
                  currencies[cardCryptodraftDetails?.principal_currency?.toUpperCase()].conversion,
                exchange.FIAT_VALUE
              )
            )}{' '}
            {fiatCurrency?.toUpperCase()}
          </p>
        </span>
      </div>
      <div className="mt-2">
        <p className="text-f6 text-shadow p-0">
          {translate('cryptodraft_settlement_select_principle_amount_note')}
        </p>
      </div>
      <FocusableInputArea className="h1 amount-input mt-4">
        <input
          required
          type="text"
          className="form-control bg-transparent text-bold p-0 text-center border-0"
          value={cryptoAmount}
          style={{
            fontSize: 'inherit',
            width: `${manualInputWidth + 1}ch`,
            color: 'inherit',
            height: 'inherit',
            textIndent: cryptoAmount ? 0 : '34%',
          }}
          ref={manualInputSpan}
          onChange={(e) => handleChangeAmount(e)}
          onFocus={handleFocus}
          onBlur={handleBlurAmount}
          placeholder="0  "
        />
        <b
          style={{
            fontSize: 'inherit',
            pointerEvents: 'none',
            color: 'inherit',
          }}>
          {coin?.toUpperCase()}
        </b>
      </FocusableInputArea>
      <div className="mb-4 position-relative">
        <div className="slider-progress">
          <div className="progress">
            <div
              className="progress-bar"
              style={{
                width: `${((cryptoAmount - minDeposit) * 100) / (maxDeposit - minDeposit)}%`,
              }}
            />
          </div>
          <input
            type="range"
            className="form-range"
            value={cryptoAmount}
            max={maxDeposit}
            min={minDeposit}
            step="0.000000001"
            onChange={(e) => {
              handleChangeAmount(e);
            }}
          />
        </div>
        {/* this lines for implementing edges for the slider */}
        <div
          className="position-absolute w-100 d-flex flex-row justify-content-between align-items-start"
          style={{ top: '15%' }}>
          {Array.from({ length: 7 }, (e, i) => i + 1).map((item) => (
            <hr
              key={item}
              className={`progress-edges text-disabled ${
                item === 1 || item === 7 ? 'progress-main-edge' : 'progress-edges'
              }`}
              style={{ transform: 'rotate(90deg)', height: '1rem !important' }}
            />
          ))}
          {Array.from({ length: 6 }, (e, i) => i + 1).map((item) => (
            <hr
              key={item}
              className={`progress-edges text-disabled ${
                item === 6 ? 'progress-main-edge' : 'progress-edges'
              }`}
              style={{ transform: 'rotate(90deg)', height: '1rem !important' }}
            />
          ))}
          {Array.from({ length: 6 }, (e, i) => i + 1).map((item) => (
            <hr
              key={item}
              className={`progress-edges text-disabled ${
                item === 6 ? 'progress-main-edge' : 'progress-edges'
              }`}
              style={{ transform: 'rotate(90deg)', height: '1rem !important' }}
            />
          ))}
          {Array.from({ length: 6 }, (e, i) => i + 1).map((item) => (
            <hr
              key={item}
              className={`progress-edges text-disabled ${
                item === 6 ? 'progress-main-edge' : 'progress-edges'
              }`}
              style={{ transform: 'rotate(90deg)', height: '1rem !important' }}
            />
          ))}
        </div>
        <div className="position-absolute w-100 d-flex flex-row justify-content-between align-items-start">
          <p className="text-f6 text-disabled">
            {`${formatNumberWithComma(
              formatNumber(parseFloat(minDeposit), exchange.FIAT_VALUE)
            )} ${coin?.toUpperCase()}`}
          </p>
          <p className="text-f6 text-disabled">
            {`${formatNumberWithComma(
              formatNumber(parseFloat(maxDeposit), exchange.CRYPTO_VALUE)
            )} ${coin?.toUpperCase()}`}
          </p>
        </div>
      </div>
      <div className="mt-4">
        <p className="text-shadow text-f6 p-0">{translate('interest_rate_label')}</p>
      </div>
      <div className="rounded-extra bg-lighter mt-3 card-calculator__summary">
        <div className="d-flex flex-row justify-content-between p-3">
          <p className="text-dark text-f6 d-flex align-items-center">
            {translate('cryptodraft_settlement_principle_owed_after_partial')}
          </p>
          <b className="text-f6">{`${formatNumberWithComma(
            formatNumber(
              calculatePrincipleOwedAfterPay(cryptoAmount) *
                collateralPrincipleCurrencyToDisplayCurrencyRate,
              exchange.FIAT_VALUE
            )
          )} ${principalCurrency?.toUpperCase()}`}</b>
        </div>
        <div className="d-flex flex-row justify-content-between p-3">
          <p className="text-dark text-f6 d-flex align-items-center">
            {translate('cryptodraft_settlement_withdrawable_collateral')}
          </p>
          <b className="text-f6">{`${formatNumber(
            !withdrawableAmount ? 0 : withdrawableAmount.withdrawable_amount,
            exchange.CRYPTO_VALUE
          )} ${cardCryptodraftDetails?.collateral_currency?.toUpperCase()}`}</b>
        </div>
      </div>
      <div className="btn-container">
        <button
          type="button"
          disabled={cryptoAmount <= 0 || !validBalance}
          loading={isLoading || isLoadingPayFullSettlement ? 'true' : 'false'}
          className="btn btn-primary py-3 w-100"
          onClick={handleSubmit}>
          {translate('pay_now_btn_title')}
        </button>
      </div>
    </MainBodyLayout>
  );
};

export default SettleCryptodraftPayPartial;
